import { PropsWithChildren, ReactNode, useId } from 'react';

/**
 * `card` is a bordered panel with a display-font heading; `dense` is a tighter card with a tinted header for
 * panels nested inside other panels (audit findings, data checks); `section` has no card at all, just a
 * divider and a small uppercase heading, for the stacked sections of an inspector.
 */
export type AdminPanelVariant = 'card' | 'dense' | 'section';

export type AdminPanelProps = PropsWithChildren<{
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Shown before the title in `dense` panels. */
  icon?: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Names the panel by an outside heading; a titled panel is otherwise named by its own title. */
  ariaLabelledBy?: string;
  /** Fills the remaining space in a flex-column page and keeps overflow inside the panel body. */
  fill?: boolean;
  /** Tighter panel chrome for embedded inspectors. */
  compact?: boolean;
  variant?: AdminPanelVariant;
}>;

const sectionClassName: Record<AdminPanelVariant, string> = {
  card: 'rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]',
  dense: 'overflow-hidden rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]',
  section: 'border-t border-[var(--ots-outline-variant)] px-4 py-3',
};

export const AdminPanel = ({
  title,
  description,
  actions,
  icon,
  className,
  bodyClassName,
  ariaLabelledBy,
  fill = false,
  compact = false,
  variant = 'card',
  children,
}: AdminPanelProps) => {
  const generatedId = useId();
  const titleId = ariaLabelledBy ?? (title ? generatedId : undefined);
  const hasHeader = !!(title || description || actions || icon);

  const header = (() => {
    if (!hasHeader) return null;
    if (variant === 'section') {
      return (
        <div className="mb-2 flex items-center justify-between gap-2">
          {title && (
            <h3 id={titleId} className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]">
              {title}
            </h3>
          )}
          {actions}
        </div>
      );
    }
    if (variant === 'dense') {
      return (
        <div className="flex items-center gap-2 border-b border-[var(--ots-outline-variant)] bg-[var(--ots-surface-variant)] px-3 py-1.5">
          {icon && (
            <span aria-hidden="true" className="size-4 shrink-0 text-[var(--ots-text-muted)] [&>svg]:size-full">
              {icon}
            </span>
          )}
          <h4 id={titleId} className="flex-1 text-xs font-bold">
            {title}
          </h4>
          {actions != null && (
            <span className="text-[11px] font-semibold text-[var(--ots-text-muted)]">{actions}</span>
          )}
        </div>
      );
    }
    return (
      <div
        className={`shrink-0 flex flex-wrap items-start justify-between gap-4 border-b border-[var(--ots-outline-variant)] ${
          compact ? 'px-4 py-3' : 'px-5 py-5 sm:px-6'
        }`}
      >
        <div>
          {title && (
            <h2
              id={titleId}
              className="font-[family-name:var(--ots-font-display)] text-base font-semibold text-[var(--ots-secondary)]"
            >
              {title}
            </h2>
          )}
          {description && (
            <p className="mt-1.5 text-sm text-[var(--ots-text-muted)]">
              {description}
            </p>
          )}
        </div>
        {actions}
      </div>
    );
  })();

  return (
    <section
      aria-labelledby={titleId}
      className={[
        'min-w-0 max-w-full',
        sectionClassName[variant],
        fill ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {header}
      <div
        className={[
          fill ? 'min-h-0 min-w-0 flex-1 overflow-hidden' : '',
          bodyClassName,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
    </section>
  );
};

/** A short centered note for a panel with nothing to show. */
export const AdminPanelEmpty = ({ children }: { children: ReactNode }) => (
  <p className="p-3 text-center text-xs text-[var(--ots-text-muted)]">{children}</p>
);
